import { useEffect, useRef, useCallback, useState } from 'react';

const IDLE_TIMEOUT_MS = 15 * 60 * 1000;  // 15 minutes
const WARN_BEFORE_MS  =  1 * 60 * 1000;  // warn 1 minute before logout

const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keypress', 'touchstart', 'scroll', 'click'];

/**
 * Tracks user idle time. Calls onWarn when WARN_BEFORE_MS remain,
 * and onIdle when the full IDLE_TIMEOUT_MS elapses without activity.
 *
 * Returns resetTimer — call it when the user explicitly chooses to stay logged in.
 */
const useIdleTimeout = ({ onWarn, onIdle, enabled }) => {
  const warnTimerRef  = useRef(null);
  const idleTimerRef  = useRef(null);

  const clearTimers = useCallback(() => {
    clearTimeout(warnTimerRef.current);
    clearTimeout(idleTimerRef.current);
  }, []);

  const resetTimer = useCallback(() => {
    clearTimers();
    if (!enabled) return;
    warnTimerRef.current = setTimeout(onWarn, IDLE_TIMEOUT_MS - WARN_BEFORE_MS);
    idleTimerRef.current = setTimeout(onIdle, IDLE_TIMEOUT_MS);
  }, [enabled, onWarn, onIdle, clearTimers]);

  useEffect(() => {
    if (!enabled) {
      clearTimers();
      return;
    }

    resetTimer();

    ACTIVITY_EVENTS.forEach(evt => window.addEventListener(evt, resetTimer, { passive: true }));

    return () => {
      clearTimers();
      ACTIVITY_EVENTS.forEach(evt => window.removeEventListener(evt, resetTimer));
    };
  }, [enabled, resetTimer, clearTimers]);

  return { resetTimer };
};

export { WARN_BEFORE_MS };
export default useIdleTimeout;
